import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import Modal from "components/DesignSystem/Modal/Modal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import Pencil from "components/icons/pencil";
import { PriceInput } from "components/PriceInput/PriceInput";
import { Form, Formik } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import {
  Summary,
  useUpdateFinancialClosingMutation,
  useUpdateFinancialClosingSummaryMutation,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

type SummaryCardProps = {
  editable?: boolean;
  summary: Summary;
  financialClosingId: string;
  entityId: string;
  showSummary?: boolean;
};

const SummaryItem = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <div className="t-flex t-justify-between t-items-center">
      <div className="t-text-body-sm t-text-text-60">{label}</div>
      <div className="t-text-subtext-sm t-text-text-100">{value}</div>
    </div>
  );
};

const EditSummaryModal = ({
  isOpen,
  close,
  financialClosingId,
  summary,
  entityId,
}: ModalProps & Omit<SummaryCardProps, "editable" | "showSummary">) => {
  const [updateSummary] = useUpdateFinancialClosingSummaryMutation();
  const { alertToast, successToast } = useToast();

  const onSubmit = async (value: Record<string, number>) => {
    try {
      await updateSummary({
        entityId,
        financialClosingId,
        summary: value,
      }).unwrap();
      close();
      successToast({ message: "Summary updated!" });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },

        error as Error
      );
    }
  };

  const initialValues = Object.entries(summary).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value.value }),
    {} as Record<string, number>
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ submitForm, isSubmitting }) => (
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Edit Summary</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <Form className="t-flex t-flex-col t-gap-4">
                {Object.entries(summary).map(([key, value]) => {
                  return (
                    <SummaryItem
                      key={key}
                      label={value.title}
                      value={<PriceInput name={key} required type="number" />}
                    />
                  );
                })}
              </Form>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button disabled={isSubmitting}>Close</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                onClick={submitForm}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};

export const SummaryCard = ({
  editable = false,
  summary,
  financialClosingId,
  entityId,
  showSummary,
}: SummaryCardProps) => {
  const editModal = useModal();
  const { alertToast } = useToast();
  const [updateFinancialClosing, { isLoading }] =
    useUpdateFinancialClosingMutation();

  const onSummaryToggleChange = async (checked: boolean) => {
    try {
      await updateFinancialClosing({
        entityId: entityId,
        financialClosingId: financialClosingId,
        payload: {
          show_summary: checked,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (!showSummary && !editable) {
    return null;
  }

  return (
    <>
      <Card.Root className="t-pb-6 t-rounded-lg sm:!t-px-3">
        <Card.Header>
          <Card.Title className="t-flex t-justify-between t-w-full t-items-center">
            Summary
            {editable && (
              <div className="t-flex t-gap-2 t-items-center">
                <Button
                  customType="ghost_icon"
                  size="small"
                  onClick={editModal.open}
                >
                  <span className="t-text-text-30">
                    <Pencil color="currentColor" size="14" />
                  </span>
                </Button>
                <Switch
                  disabled={isLoading}
                  size="small"
                  defaultChecked={showSummary}
                  onCheckedChange={onSummaryToggleChange}
                />
              </div>
            )}
          </Card.Title>
        </Card.Header>
        <Card.Body className="t-flex t-flex-col t-gap-4">
          {Object.entries(summary).map(([key, value]) => (
            <SummaryItem
              key={key}
              label={value.title}
              value={<AmountSuperScript amount={value.value} />}
            />
          ))}
        </Card.Body>
      </Card.Root>
      <EditSummaryModal
        financialClosingId={financialClosingId}
        summary={summary}
        isOpen={editModal.isOpen}
        close={editModal.close}
        entityId={entityId}
      />
    </>
  );
};
